import type { FaqItem } from "@/content/faq";

export type ComparisonProduct = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  websiteUrl?: string;
};

export type ComparisonTableRow = {
  label: string;
  passiveAgents: string;
  competitor: string;
};

export type BestForItem = {
  product: "passiveagents" | "competitor";
  heading: string;
  points: string[];
};

export type KeyDifference = {
  heading: string;
  passiveAgents: string;
  competitor: string;
};

export type ComparisonCta = {
  heading: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
};

export type ComparisonSeo = {
  title: string;
  description: string;
  slug: string;
};

export type ComparisonData = {
  competitor: ComparisonProduct;
  h1: string;
  shortAnswer: string;
  quickComparisonTable: ComparisonTableRow[];
  bestFor: BestForItem[];
  keyDifferences: KeyDifference[];
  whenToUseCompetitor: string[];
  whenToUsePassiveAgents: string[];
  canYouUseBoth: string;
  faq: FaqItem[];
  cta: ComparisonCta;
  seo: ComparisonSeo;
};

export const vsCursor: ComparisonData = {
  competitor: {
    id: "cursor",
    name: "Cursor",
    tagline: "AI-first code editor",
    description:
      "Cursor is a code editor built from the ground up for AI-assisted development. It uses Claude and GPT models to help you write, edit, and understand code inline as you type.",
  },
  seo: {
    title: "PassiveAgents vs Cursor: What's the difference?",
    description:
      "Compare PassiveAgents and Cursor. Cursor helps you code faster in real time. PassiveAgents lets AI agents work on tasks while you sleep.",
    slug: "vs-cursor",
  },
  h1: "PassiveAgents vs Cursor",
  shortAnswer:
    "Cursor is a code editor with built-in AI assistance that helps you code faster while you're actively at your keyboard. PassiveAgents is an orchestration layer that lets AI coding agents work on your tasks in the background, even when you step away. They complement each other: Cursor helps you write code, and PassiveAgents helps agents run on their own.",
  quickComparisonTable: [
    {
      label: "What it is",
      passiveAgents: "Agent orchestration layer",
      competitor: "AI-powered code editor",
    },
    {
      label: "Primary use",
      passiveAgents: "Run AI agents on tasks in the background",
      competitor: "Write and edit code with AI assistance inline",
    },
    {
      label: "Works while you are away",
      passiveAgents: "Yes. Agents keep running when you close the browser.",
      competitor:
        "Background Agents can run async tasks, but work stays inside Cursor's workspace.",
    },
    {
      label: "Multiple task support",
      passiveAgents: "Multiple agents work on different tasks in parallel.",
      competitor:
        "Multiple background agents are supported, but managed within Cursor's own environment.",
    },
    {
      label: "Project management built in",
      passiveAgents: "Yes. Kanban board, morning summaries, agent livestreams.",
      competitor:
        "Background agent status visible in sidebar with web/mobile review. No Kanban board, morning summaries, or multi-runtime support.",
    },
    {
      label: "Pricing model",
      passiveAgents: "Flat subscription. Unused agent capacity rolls over.",
      competitor: "Per-user subscription with usage-based add-ons.",
    },
  ],
  bestFor: [
    {
      product: "competitor",
      heading: "Cursor is best for",
      points: [
        "Developers who want AI autocomplete and inline suggestions while they write code",
        "People who prefer a dedicated editor over editor extensions",
        "Teams that want AI features deeply integrated into the editing environment",
      ],
    },
    {
      product: "passiveagents",
      heading: "PassiveAgents is best for",
      points: [
        "Running AI coding agents on tasks while you focus on other work",
        "Orchestrating multiple agents across different runtimes (Claude Code, Codex, Gemini CLI, OpenCode)",
        "Reviewing agent progress from a project board or your phone",
      ],
    },
  ],
  keyDifferences: [
    {
      heading: "Interaction model",
      passiveAgents:
        "You create tasks, assign agents, and review outputs. Agents do the work asynchronously.",
      competitor:
        "You write code with AI suggestions appearing in real time as you type. The interaction is synchronous.",
    },
    {
      heading: "Scope of work",
      passiveAgents:
        "Multiple agents can work on different tasks at the same time across multiple repositories.",
      competitor:
        "Cursor Background Agents run async tasks, clone GitHub repos, push branches, and support PR review. Work is scoped to Cursor's own AI models.",
    },
    {
      heading: "Presence requirement",
      passiveAgents:
        "Agents continue working after you close the browser. You can check results from your phone.",
      competitor:
        "Cursor Background Agents run asynchronously with web/mobile status and follow-up support — but through Cursor's environment, not interchangeable local runtimes.",
    },
    {
      heading: "Agent runtime support",
      passiveAgents:
        "Orchestrates Claude Code, Codex, Gemini CLI, and OpenCode as interchangeable agents.",
      competitor:
        "Uses its own AI models (Claude and GPT) integrated into the editor.",
    },
  ],
  whenToUseCompetitor: [
    "You want AI autocomplete and inline editing as you write code.",
    "You prefer a dedicated editor built specifically for AI-assisted development.",
    "You need real-time AI suggestions while actively coding.",
  ],
  whenToUsePassiveAgents: [
    "You want AI agents to keep working on tasks after you close your laptop.",
    "You need to run multiple tasks at the same time using different coding agents.",
    "You want to check agent progress from a project board or your phone.",
  ],
  canYouUseBoth:
    "Yes. Cursor and PassiveAgents solve different problems. Use Cursor to write code with AI assistance at your keyboard. Use PassiveAgents to let agents run on their own in the background. Many developers use both: write with Cursor, orchestrate with PassiveAgents.",
  faq: [
    {
      question: "Can PassiveAgents replace Cursor?",
      answer:
        "No. They serve different purposes. Cursor is an editor for writing code with AI assistance in real time. PassiveAgents is an orchestration layer that runs AI agents asynchronously. You might use both for different parts of your workflow.",
    },
    {
      question: "Does PassiveAgents work with Cursor?",
      answer:
        "PassiveAgents orchestrates Claude Code, Codex, Gemini CLI, and OpenCode. It does not integrate with Cursor directly. But you can use Cursor to write code and PassiveAgents to run tasks using other coding agents in the background.",
    },
    {
      question: "Which is better for solo developers?",
      answer:
        "It depends on what you need. If you want AI help while you actively code, Cursor is a strong choice. If you want agents to work on tasks while you are away, PassiveAgents fills that gap. They address different bottlenecks.",
    },
    {
      question: "Which is better for teams?",
      answer:
        "Cursor works well for individual developers who want AI-assisted editing. PassiveAgents helps teams run multiple agents on multiple tasks in parallel, with shared visibility through the project board. They are not direct competitors.",
    },
  ],
  cta: {
    heading: "Run agents while you sleep",
    description:
      "PassiveAgents coordinates your coding agents into a system that works even when you are not at your desk.",
    buttonText: "Start free trial",
    buttonUrl: "/pricing",
  },
};

export const vsClaudeCode: ComparisonData = {
  competitor: {
    id: "claude-code",
    name: "Claude Code",
    tagline: "AI coding agent from Anthropic",
    description:
      "Claude Code is Anthropic's terminal-based AI coding agent. It reads, writes, and edits code, runs terminal commands, and manages multi-step software engineering tasks through a conversational interface.",
  },
  seo: {
    title: "PassiveAgents vs Claude Code: What's the difference?",
    description:
      "Compare PassiveAgents and Claude Code. Claude Code is a coding agent you guide interactively. PassiveAgents orchestrates Claude Code alongside other agents to run on tasks in the background.",
    slug: "vs-claude-code",
  },
  h1: "PassiveAgents vs Claude Code",
  shortAnswer:
    "Claude Code is a coding agent you guide interactively in your terminal. PassiveAgents is the orchestration layer that lets Claude Code and other coding agents work on your tasks even after you close the terminal. PassiveAgents runs Claude Code as one of its supported agent runtimes.",
  quickComparisonTable: [
    {
      label: "What it is",
      passiveAgents: "Agent orchestration layer",
      competitor: "Terminal-based AI coding agent",
    },
    {
      label: "Primary use",
      passiveAgents: "Coordinate agents to work on tasks in the background",
      competitor: "Interactive coding assistant in your terminal",
    },
    {
      label: "Works while you are away",
      passiveAgents: "Yes. Agents run independently after you disconnect.",
      competitor: "Not by itself. Requires active terminal session.",
    },
    {
      label: "Multiple agent support",
      passiveAgents:
        "Orchestrates Claude Code, Codex, Gemini CLI, and OpenCode together.",
      competitor: "Single agent session per terminal.",
    },
    {
      label: "Project management",
      passiveAgents:
        "Kanban board, task queue, morning summaries, livestreams.",
      competitor: "No project management features. Pure coding agent.",
    },
    {
      label: "How they relate",
      passiveAgents:
        "PassiveAgents can run Claude Code as one of its agent runtimes.",
      competitor:
        "Claude Code is a coding agent that PassiveAgents orchestrates.",
    },
  ],
  bestFor: [
    {
      product: "competitor",
      heading: "Claude Code is best for",
      points: [
        "Developers who want a conversational AI coding agent in their terminal",
        "Working through complex coding tasks with step-by-step AI guidance",
        "Teams that want Anthropic's Claude model directly integrated into their development workflow",
      ],
    },
    {
      product: "passiveagents",
      heading: "PassiveAgents is best for",
      points: [
        "Running Claude Code and other agents on tasks without needing an active terminal session",
        "Managing multiple concurrent tasks with different agents across different runtimes",
        "Getting a birds-eye view of what all your agents are doing from a single project board",
      ],
    },
  ],
  keyDifferences: [
    {
      heading: "Interaction model",
      passiveAgents:
        "You create tasks, assign agents, and review outputs. Agents run asynchronously. You are the manager, not the operator.",
      competitor:
        "You chat with Claude Code in your terminal, guiding it through tasks step by step. You are the operator.",
    },
    {
      heading: "Persistence",
      passiveAgents:
        "Agents keep working after you close the browser or terminal. Results are waiting for you when you check back.",
      competitor:
        "Claude Code stops when you close the terminal. Work resumes only when you start a new session.",
    },
    {
      heading: "Multi-tasking",
      passiveAgents:
        "Multiple agents work on different tasks in parallel. Each agent can use a different runtime (Claude Code, Codex, etc.).",
      competitor:
        "One session at a time. Running multiple instances requires managing separate terminals manually.",
    },
    {
      heading: "Management layer",
      passiveAgents:
        "Kanban board, task priority queue, morning summaries, agent livestreams, mobile progress checks.",
      competitor:
        "No management layer. Claude Code is a pure coding agent with no orchestration features.",
    },
  ],
  whenToUseCompetitor: [
    "You want to work through a coding task interactively with AI guidance in your terminal.",
    "You prefer a conversational workflow where you direct the agent step by step.",
    "You are already using Claude Code and want to use it directly.",
  ],
  whenToUsePassiveAgents: [
    "You want Claude Code to keep working on tasks after you finish your terminal session.",
    "You need to run tasks using multiple different coding agents in parallel.",
    "You want project management features around your agent workflow.",
  ],
  canYouUseBoth:
    "Yes. PassiveAgents actually runs Claude Code as one of its supported agent runtimes. You can use Claude Code interactively in your terminal for hands-on tasks, and let PassiveAgents orchestrate Claude Code (along with other agents) for background tasks. They are complementary by design.",
  faq: [
    {
      question: "Does PassiveAgents replace Claude Code?",
      answer:
        "No. PassiveAgents orchestrates Claude Code. It lets Claude Code work on tasks in the background, but Claude Code remains the coding agent that does the actual code work.",
    },
    {
      question: "Do I need Claude Code to use PassiveAgents?",
      answer:
        "No. PassiveAgents supports Claude Code, Codex, Gemini CLI, and OpenCode. You can use any combination of these runtimes. Claude Code is optional.",
    },
    {
      question: "Can I use Claude Code and PassiveAgents at the same time?",
      answer:
        "Yes. Use Claude Code interactively in your terminal for tasks you want to guide step by step. Let PassiveAgents run Claude Code on other tasks in the background. They do not conflict.",
    },
    {
      question: "Which is more autonomous?",
      answer:
        "PassiveAgents adds autonomy by running coding agents independently. Claude Code by itself needs an active terminal session. Combined with PassiveAgents, Claude Code can work on tasks while you are away.",
    },
  ],
  cta: {
    heading: "Let Claude Code work while you sleep",
    description:
      "PassiveAgents runs Claude Code and other coding agents on your tasks around the clock.",
    buttonText: "Start free trial",
    buttonUrl: "/pricing",
  },
};

export const vsCodex: ComparisonData = {
  competitor: {
    id: "codex",
    name: "Codex",
    tagline: "AI coding agent from OpenAI",
    description:
      "Codex is OpenAI's terminal-based AI coding agent. It writes and edits code, runs commands, and handles software engineering tasks through a collaborative terminal interface powered by OpenAI models.",
  },
  seo: {
    title: "PassiveAgents vs Codex: What's the difference?",
    description:
      "Compare PassiveAgents and Codex. Codex is an AI coding agent you work with interactively. PassiveAgents orchestrates Codex alongside other agents to work on tasks in the background.",
    slug: "vs-codex",
  },
  h1: "PassiveAgents vs Codex",
  shortAnswer:
    "Codex is OpenAI's coding agent you collaborate with in your terminal. PassiveAgents is the orchestration layer that lets Codex and other agents work on tasks even when you are not at your keyboard. PassiveAgents runs Codex as one of its supported agent runtimes.",
  quickComparisonTable: [
    {
      label: "What it is",
      passiveAgents: "Agent orchestration layer",
      competitor: "Terminal-based AI coding agent from OpenAI",
    },
    {
      label: "Primary use",
      passiveAgents: "Coordinate agents to work on tasks in the background",
      competitor: "Interactive coding assistant in your terminal",
    },
    {
      label: "Works while you are away",
      passiveAgents: "Yes. Agents run independently.",
      competitor: "Not by itself. Requires active session.",
    },
    {
      label: "Multiple agent support",
      passiveAgents:
        "Orchestrates Codex, Claude Code, Gemini CLI, and OpenCode together.",
      competitor: "Single agent session at a time.",
    },
    {
      label: "Project management",
      passiveAgents:
        "Kanban board, task queue, morning summaries, livestreams.",
      competitor: "Pure coding agent. No project management.",
    },
    {
      label: "How they relate",
      passiveAgents:
        "PassiveAgents can run Codex as one of its agent runtimes.",
      competitor: "Codex is an agent that PassiveAgents orchestrates.",
    },
  ],
  bestFor: [
    {
      product: "competitor",
      heading: "Codex is best for",
      points: [
        "Developers who prefer OpenAI models for AI-assisted coding",
        "Working through coding tasks with a collaborative terminal-based agent",
        "Teams already using OpenAI ecosystem tools",
      ],
    },
    {
      product: "passiveagents",
      heading: "PassiveAgents is best for",
      points: [
        "Running Codex and other agents on tasks without an active terminal session",
        "Managing multiple code tasks across different runtimes from one board",
        "Reviewing agent work through morning summaries and mobile notifications",
      ],
    },
  ],
  keyDifferences: [
    {
      heading: "Interaction model",
      passiveAgents:
        "Asynchronous task-based workflow. Define tasks, assign agents, review outputs.",
      competitor:
        "Interactive terminal session. You guide Codex through tasks in real time.",
    },
    {
      heading: "Persistence",
      passiveAgents:
        "Agents keep working when you are away. Results accumulate on the project board.",
      competitor:
        "Codex stops when the terminal closes. Work must be done in one session or manually resumed.",
    },
    {
      heading: "Multi-runtime orchestration",
      passiveAgents:
        "Codex, Claude Code, Gemini CLI, and OpenCode can all run as agents in the same system.",
      competitor:
        "Codex is a standalone agent. No built-in orchestration of other runtimes.",
    },
    {
      heading: "Visibility",
      passiveAgents:
        "See all agent activity from a single dashboard. Watch livestreams, check summaries from your phone.",
      competitor:
        "Visibility is limited to the active terminal session. No aggregated view of work.",
    },
  ],
  whenToUseCompetitor: [
    "You want to collaborate with an AI coding agent interactively in your terminal.",
    "You prefer OpenAI models and want a dedicated coding agent built on them.",
    "You are already using Codex and want to work through tasks directly.",
  ],
  whenToUsePassiveAgents: [
    "You want Codex to keep working on tasks after you close the terminal.",
    "You need to run multiple tasks using different coding agents in parallel.",
    "You want project management features like task queues and progress summaries.",
  ],
  canYouUseBoth:
    "Yes. PassiveAgents runs Codex as one of its supported agent runtimes. Use Codex interactively for hands-on work, and let PassiveAgents run Codex on other tasks in the background.",
  faq: [
    {
      question: "Does PassiveAgents replace Codex?",
      answer:
        "No. PassiveAgents orchestrates Codex. Codex remains the coding agent that does the actual work. PassiveAgents handles the scheduling, management, and coordination.",
    },
    {
      question: "Do I need Codex to use PassiveAgents?",
      answer:
        "No. PassiveAgents supports Codex, Claude Code, Gemini CLI, and OpenCode. You can pick any combination.",
    },
    {
      question:
        "Can I use Codex interactively and with PassiveAgents at the same time?",
      answer:
        "Yes. Codex can run both interactively in your terminal and as a managed agent through PassiveAgents. They use the same installation.",
    },
    {
      question: "Which gives more control over the work?",
      answer:
        "Codex gives you direct control through its interactive terminal session. PassiveAgents adds a management layer on top, giving you control through task definitions, agent assignments, and output review.",
    },
  ],
  cta: {
    heading: "Run Codex around the clock",
    description:
      "PassiveAgents orchestrates Codex and other coding agents to work on your tasks continuously.",
    buttonText: "Start free trial",
    buttonUrl: "/pricing",
  },
};

export const vsDevin: ComparisonData = {
  competitor: {
    id: "devin",
    name: "Devin",
    tagline: "Autonomous AI software engineer",
    description:
      "Devin is an autonomous AI software engineer from Cognition Labs. It plans, codes, tests, and deploys software independently using its own development environment, IDE, and browser.",
  },
  seo: {
    title: "PassiveAgents vs Devin: What's the difference?",
    description:
      "Compare PassiveAgents and Devin. Devin is an autonomous AI engineer that works in its own environment. PassiveAgents orchestrates your local coding agents to work on tasks you define.",
    slug: "vs-devin",
  },
  h1: "PassiveAgents vs Devin",
  shortAnswer:
    "Devin is an autonomous AI software engineer that works in its own hosted environment. PassiveAgents is an orchestration layer that runs your local coding agents on tasks you control. Devin is a single autonomous worker. PassiveAgents lets you coordinate multiple agents across your own codebase.",
  quickComparisonTable: [
    {
      label: "What it is",
      passiveAgents: "Agent orchestration layer for local coding agents",
      competitor: "Autonomous AI software engineer (hosted)",
    },
    {
      label: "Where it runs",
      passiveAgents: "On your laptop. Your code and API keys stay with you.",
      competitor:
        "On Devin's cloud infrastructure. Code is uploaded to their servers.",
    },
    {
      label: "Multiple agent support",
      passiveAgents:
        "Orchestrates Claude Code, Codex, Gemini CLI, and OpenCode together.",
      competitor: "Single autonomous agent per session.",
    },
    {
      label: "Pricing model",
      passiveAgents: "Flat subscription starting at $20/month.",
      competitor:
        "Tiered pricing with self-serve plans. Check devin.ai for current rates.",
    },
    {
      label: "Who controls the environment",
      passiveAgents:
        "You control the local environment. Agents use your tools and configurations.",
      competitor: "Devin controls its own hosted environment.",
    },
    {
      label: "Target user",
      passiveAgents:
        "Individual developers and small teams who want to run agents on their own machines.",
      competitor:
        "Teams that want a fully managed autonomous engineering service.",
    },
  ],
  bestFor: [
    {
      product: "competitor",
      heading: "Devin is best for",
      points: [
        "Teams that want a fully managed autonomous AI engineer that works in its own environment",
        "Projects where uploading code to a cloud service is acceptable",
        "Organizations that can invest in higher-cost autonomous engineering solutions",
      ],
    },
    {
      product: "passiveagents",
      heading: "PassiveAgents is best for",
      points: [
        "Running multiple coding agents on your own machine with full control over your code",
        "Orchestrating different coding runtimes as a coordinated team",
        "Smaller teams and solo developers who want autonomous agent capabilities at a lower price point",
      ],
    },
  ],
  keyDifferences: [
    {
      heading: "Architecture",
      passiveAgents:
        "PassiveAgents orchestrates existing coding agents (Claude Code, Codex, etc.) that run on your local machine. Your source files stay on your machine; PassiveAgents handles task metadata and coordination.",
      competitor:
        "Devin is a self-contained autonomous engineer that runs in Cognition's cloud environment. You provide access to your repositories.",
    },
    {
      heading: "Agent count and coordination",
      passiveAgents:
        "Multiple agents work on different tasks in parallel, each using their own runtime. You manage them from a single project board.",
      competitor:
        "One Devin instance works on one task at a time. Multiple instances require separate subscriptions.",
    },
    {
      heading: "Pricing and accessibility",
      passiveAgents:
        "$20/month for up to 20 agents and 200 active tasks. Free trial available with no credit card required.",
      competitor:
        "Multiple tiers including self-serve options. Check devin.ai/pricing for current rates.",
    },
    {
      heading: "Customization",
      passiveAgents:
        "You choose which coding agents to use, which models they access, and exactly what directories they work in.",
      competitor:
        "Devin operates in its own sandboxed environment. You define goals, but Devin controls how it achieves them.",
    },
  ],
  whenToUseCompetitor: [
    "You want a fully managed autonomous engineer that handles planning, coding, and deployment in its own environment.",
    "Your organization has budget for higher-cost autonomous engineering tools.",
    "You are comfortable uploading your code to a cloud service for AI processing.",
  ],
  whenToUsePassiveAgents: [
    "You want source files to stay on your machine — PassiveAgents handles coordination without accessing your code.",
    "You want to orchestrate multiple coding agents working on different tasks in parallel.",
    "You want autonomous agent capabilities at a lower price point.",
  ],
  canYouUseBoth:
    "Potentially, but they serve overlapping needs in different ways. Devin is a complete autonomous engineer in a hosted environment. PassiveAgents orchestrates your local agents. Both let you automate software work, but they differ in where the work happens, how many agents you can run, and how much control you have over the environment.",
  faq: [
    {
      question: "Is Devin a competitor to PassiveAgents?",
      answer:
        "Partially. Both let you automate software development work. Devin is a single hosted autonomous engineer. PassiveAgents orchestrates multiple local agents. They differ in architecture, pricing, and control model.",
    },
    {
      question: "Which is more affordable?",
      answer:
        "PassiveAgents starts at $20/month with a free trial. Devin has multiple self-serve tiers — check devin.ai for current pricing. PassiveAgents is designed for smaller teams and individual developers.",
    },
    {
      question: "Which keeps my code more private?",
      answer:
        "PassiveAgents runs agents locally and does not host your repository. Note: the coding agent runtimes you connect (Claude Code, Codex, etc.) may send context to their respective providers as part of normal operation. Devin runs in Cognition's cloud environment and requires granting it access to your repository.",
    },
    {
      question: "Can PassiveAgents do what Devin does?",
      answer:
        "They approach the problem differently. Devin is a single hosted autonomous engineer. PassiveAgents coordinates multiple local coding agents. The best choice depends on your budget, privacy requirements, and preferred workflow.",
    },
  ],
  cta: {
    heading: "Orchestrate your own agent team",
    description:
      "PassiveAgents runs multiple coding agents on your machine for a fraction of the cost.",
    buttonText: "Start free trial",
    buttonUrl: "/pricing",
  },
};

export const vsJules: ComparisonData = {
  competitor: {
    id: "jules",
    name: "Jules",
    tagline: "AI coding agent from Google Gemini",
    description:
      "Jules is Google's AI coding agent, built on Gemini. It helps developers with coding tasks directly in GitHub workflows, offering AI-powered code generation, review, and debugging.",
  },
  seo: {
    title: "PassiveAgents vs Jules (Gemini): What's the difference?",
    description:
      "Compare PassiveAgents and Jules. Jules is Google's Gemini-powered coding agent for GitHub. PassiveAgents orchestrates Gemini CLI alongside other coding agents to work on tasks in the background.",
    slug: "vs-jules",
  },
  h1: "PassiveAgents vs Jules (Gemini CLI)",
  shortAnswer:
    "Jules is Google's GitHub-integrated coding agent powered by Gemini. PassiveAgents supports Gemini CLI as one of several local agent runtimes and orchestrates it alongside Claude Code, Codex, and OpenCode. They serve different workflows: Jules works inside GitHub; PassiveAgents runs agents locally on your own machine.",
  quickComparisonTable: [
    {
      label: "What it is",
      passiveAgents: "Agent orchestration layer",
      competitor: "AI coding agent from Google, integrated with GitHub",
    },
    {
      label: "Primary use",
      passiveAgents: "Coordinate agents to work on tasks in the background",
      competitor: "Code generation and review within GitHub workflows",
    },
    {
      label: "Works while you are away",
      passiveAgents: "Yes. Agents run independently.",
      competitor: "Integrated into GitHub, works asynchronously through PRs.",
    },
    {
      label: "Multiple agent support",
      passiveAgents:
        "Orchestrates Gemini CLI, Claude Code, Codex, and OpenCode together.",
      competitor: "Single agent focused on GitHub workflows.",
    },
    {
      label: "Where it works",
      passiveAgents: "On your local machine across any repository or project.",
      competitor: "Inside GitHub for repositories where it is enabled.",
    },
    {
      label: "How they relate",
      passiveAgents:
        "PassiveAgents supports Gemini CLI as one of its local agent runtimes.",
      competitor:
        "Jules is Google's separate GitHub-integrated agent powered by Gemini. Not the same as Gemini CLI.",
    },
  ],
  bestFor: [
    {
      product: "competitor",
      heading: "Jules is best for",
      points: [
        "Developers who want AI coding assistance directly inside GitHub",
        "Teams that want automated PR reviews and code generation within their GitHub workflow",
        "Users invested in the Google and Gemini ecosystem",
      ],
    },
    {
      product: "passiveagents",
      heading: "PassiveAgents is best for",
      points: [
        "Running Gemini CLI and other agents on tasks outside of GitHub",
        "Managing multiple coding agents across different projects from one board",
        "Running agents on local repositories without depending on a specific platform",
      ],
    },
  ],
  keyDifferences: [
    {
      heading: "Platform integration",
      passiveAgents:
        "Works with any project on your local machine. Not tied to a specific platform.",
      competitor:
        "Deeply integrated into GitHub. Works on repositories where Jules is enabled.",
    },
    {
      heading: "Agent runtime support",
      passiveAgents:
        "Supports Gemini CLI alongside Claude Code, Codex, and OpenCode as interchangeable agent runtimes.",
      competitor:
        "Jules uses Gemini models exclusively and operates within GitHub.",
    },
    {
      heading: "Workflow model",
      passiveAgents:
        "Create tasks on a board, assign agents, review outputs. Asynchronous and multi-agent.",
      competitor:
        "Create and review PRs through GitHub. Single-agent, PR-based workflow.",
    },
    {
      heading: "Local vs. cloud",
      passiveAgents: "Agents run on your machine. Your code stays local.",
      competitor:
        "Jules fetches the repo, clones it to a Google Cloud VM, and creates diffs and PRs. Code is processed outside your machine.",
    },
  ],
  whenToUseCompetitor: [
    "You want AI coding assistance built directly into your GitHub workflow.",
    "Your team uses GitHub and wants automated PR generation and review.",
    "You prefer a platform-integrated approach to AI-assisted development.",
  ],
  whenToUsePassiveAgents: [
    "You want to run Gemini CLI and other agents on tasks outside of GitHub.",
    "You need a single management interface for multiple coding agents across different runtimes.",
    "You want agents to run on your local machine and keep your code there.",
  ],
  canYouUseBoth:
    "Yes. Jules handles GitHub-specific coding workflows. PassiveAgents runs Gemini CLI alongside other agents for broader local task orchestration. Both use Gemini models but are different products with different workflows and model access — they address different parts of the development lifecycle.",
  faq: [
    {
      question: "Is Jules the same as Gemini CLI?",
      answer:
        "Not exactly. Jules is Google's GitHub-integrated coding agent. Gemini CLI is the terminal-based tool that PassiveAgents can orchestrate. Both use Gemini models but serve different workflows.",
    },
    {
      question: "Does PassiveAgents support Jules?",
      answer:
        "PassiveAgents supports Gemini CLI as a local agent runtime. Jules is Google's separate GitHub-integrated agent powered by Gemini — both use Gemini models but are different products with different model access by plan. For GitHub-specific workflows, Jules may be more directly useful.",
    },
    {
      question: "Which is better for GitHub-based teams?",
      answer:
        "Jules is designed specifically for GitHub integration. PassiveAgents is better for orchestrating multiple agents across different runtimes and projects, including but not limited to GitHub repos.",
    },
    {
      question: "Can I use PassiveAgents with Google's Gemini models?",
      answer:
        "Yes. PassiveAgents supports Gemini CLI as an agent runtime, which uses Google's Gemini models.",
    },
  ],
  cta: {
    heading: "Run Gemini CLI on your terms",
    description:
      "PassiveAgents orchestrates Gemini CLI and other coding agents to work on your tasks continuously.",
    buttonText: "Start free trial",
    buttonUrl: "/pricing",
  },
};

export const allComparisons: ComparisonData[] = [
  vsCursor,
  vsClaudeCode,
  vsCodex,
  vsDevin,
  vsJules,
];
